import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {useAuth} from "../../context/AuthContext";
import {useLocation} from "wouter";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    FormDescription,
} from "../ui/form";
import {Input} from "../ui/input";
import {Checkbox} from "../ui/checkbox";
import {Button} from "../ui/button";
import {Separator} from "../ui/separator";
import {useToast} from "../../hooks/use-toast";
import {RegisterData} from "../../types";
import {googleAuth} from "../../api/api";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "../ui/accordion";

interface RegisterFormProps {
    onSuccess?: () => void;
}

const registerSchema = z.object({
    firstName: z.string().min(1, {message: "First name is required"}),
    lastName: z.string().min(1, {message: "Last name is required"}),
    email: z.string().email({message: "Invalid email address"}),
    password: z
        .string()
        .min(8, {message: "Password must be at least 8 characters"}),
    username: z.string().min(3, {message: "Username must be at least 3 characters"}),
    referralCode: z.string().optional(),
    acceptTerms: z.boolean().refine((val) => val === true, {
        message: "You must accept the terms and privacy policy",
    }),
});

type RegisterFormValues = z.infer<typeof registerSchema>;

const RegisterForm = ({onSuccess}: RegisterFormProps) => {
    const {registerUser} = useAuth();
    const {toast} = useToast();
    const [, setLocation] = useLocation();

    const form = useForm<RegisterFormValues>({
        resolver: zodResolver(registerSchema),
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            username: "",
            referralCode: "",
            acceptTerms: false,
        },
    });

    const acceptTermsChecked = form.watch("acceptTerms");

    const onSubmit = async (data: RegisterFormValues) => {
        try {
            const registerData: RegisterData = {
                email: data.email,
                password: data.password,
                first_name: data.firstName,
                last_name: data.lastName,
                username: data.username,
                is_restaurant_owner: false,
                referred_by: data.referralCode || undefined,
            }
            const user = await registerUser(registerData);

            if (onSuccess) {
                onSuccess();
            }
            setLocation(`/activate?userId=${user.id}`);
        } catch (error) {
            console.error("Registration error:", error);
            // Toast is already handled in the mutation
        }
    };

    const handleSocialRegister = (provider: string) => {
        toast({
            title: `${provider} registration`,
            description: "This feature is coming soon!",
        });
    };

    const handleGoogleRegister = () => {
        const g = (window as any).google;
        if (!g?.accounts?.oauth2) {
            toast({
                title: "Google auth failed",
                description: "Google SDK not loaded",
                variant: "destructive",
            });
            return;
        }

        const client = g.accounts.oauth2.initCodeClient({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID as string,
            scope: "openid email profile",
            ux_mode: "popup",
            callback: async (resp: any) => {
                if (resp.code) {
                    try {
                        const res = await googleAuth(resp.code);
                        toast({
                            title: "Success!",
                            description: "You have registered with Google.",
                        });
                        onSuccess?.();
                    } catch (err: any) {
                        const errMsg =
                            err.response?.data?.error ||
                            err.message ||
                            "Google auth failed";
                        toast({
                            title: "Google auth failed",
                            description: errMsg,
                            variant: "destructive",
                        });
                    }
                }
            },
        });
        client.requestCode();
    };


    return (
        <>
            <h2 className="mb-6 text-2xl font-bold text-gray-800 dark:text-white">
                Create Your Account
            </h2>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <FormField
                            control={form.control}
                            name="firstName"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>First Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="John" {...field} />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="lastName"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>Last Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Doe" {...field} />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                    </div>

                    <FormField
                        control={form.control}
                        name="username"
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>Username</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="johndoe"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="email"
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>Email</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="your@email.com"
                                        type="email"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="password"
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>Password</FormLabel>
                                <FormControl>
                                    <Input placeholder="••••••••" type="password" {...field} />
                                </FormControl>
                                <FormDescription className="text-xs">
                                    Password must be at least 8 characters long
                                </FormDescription>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="referral">
                            <AccordionTrigger>
                                Do you have a referral code?
                            </AccordionTrigger>
                            <AccordionContent>
                                <FormField
                                    control={form.control}
                                    name="referralCode"
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel>Referral Code (Optional)</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter code" {...field} />
                                            </FormControl>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>

                    <FormField
                        control={form.control}
                        name="acceptTerms"
                        render={({field}) => (
                            <FormItem className="flex items-start space-x-2 space-y-0">
                                <FormControl>
                                    <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                    />
                                </FormControl>
                                <div className="space-y-1 leading-none">
                                    <FormLabel className="text-sm font-normal">
                                        I agree to the{" "}
                                        <a
                                            target="_blank"
                                            href="/terms"
                                            className="text-primary hover:underline dark:text-primary-dark"
                                        >
                                            Terms
                                        </a>{" "}
                                        and{" "}
                                        <a
                                            target="_blank"
                                            href="/privacy"
                                            className="text-primary hover:underline dark:text-primary-dark"
                                        >
                                            Privacy Policy
                                        </a>
                                    </FormLabel>
                                    <FormMessage/>
                                </div>
                            </FormItem>
                        )}
                    />

                    <Button
                        type="submit"
                        className="w-full bg-primary font-medium text-white hover:bg-red-700"
                        disabled={!acceptTermsChecked || form.formState.isSubmitting}
                    >
                        {form.formState.isSubmitting ? "Creating account..." : "Create Account"}
                    </Button>
                </form>
            </Form>

            <div className="mt-4">
                {/*    <div className="relative">*/}
                {/*        <div className="absolute inset-0 flex items-center">*/}
                {/*            <Separator className="w-full"/>*/}
                {/*        </div>*/}
                {/*        <div className="relative flex justify-center text-sm">*/}
                {/*<span className="bg-white px-2 text-gray-500 dark:bg-gray-800 dark:text-gray-400">*/}
                {/*  Or register with*/}
                {/*</span>*/}
                {/*        </div>*/}
                {/*    </div>*/}

                <div className="mt-4 grid grid-cols-2 gap-3">
                    <Button
                        variant="outline"
                        onClick={handleGoogleRegister}
                        className="flex items-center justify-center"
                        type="button"
                        disabled={!acceptTermsChecked} // <--- Disable if terms not accepted
                    >
                        <svg
                            className="mr-2 h-4 w-4 text-red-500"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 488 512"
                            fill="currentColor"
                        >
                            <path
                                d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"/>
                        </svg>
                        <span>Google</span>
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => handleSocialRegister("Facebook")}
                        className="flex items-center justify-center"
                        disabled={!acceptTermsChecked}
                    >
                        <svg
                            className="mr-2 h-4 w-4 text-blue-600"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 320 512"
                            fill="currentColor"
                        >
                            <path
                                d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"/>
                        </svg>
                        <span>Facebook</span>
                    </Button>
                </div>
            </div>
        </>
    );
};

export default RegisterForm;
